/**
 * AstralProjectionSkillEffect — Astral oracle-path body/projection separation.
 *
 * The player separates their projection from their body for up to 4 seconds
 * (extendable to 6s with Combat Anchor). Body stays locked at the activation
 * point and is invincible. The projection entity moves freely, passing through
 * walls and enemies, marking up to 3 targets (4 with Combat Anchor).
 *
 * On return (natural expiry or manual re-activation), each marked enemy
 * receives an `astral_mark` buff. The player's next skill hit on a marked
 * enemy deals 15% bonus damage and consumes the mark.
 *
 * Upgrade hooks:
 * - astral_projection_upgrade_1 (Combat Anchor):
 *     `durationMs` (additive, scope: astral_projection) — +2000ms window.
 *     `maxMarks` (additive, scope: astral_projection) — +1 mark cap.
 * - astral_projection_upgrade_2 (Deathmark):
 *     `markIntentionRevealDurationMs` (max, scope: astral_projection) — if
 *     non-zero, calls ForesightRenderSystem.showExtendedArcs() so all marked
 *     enemies render attack arcs continuously for the specified window.
 * - astral_projection_passive (Haunted): handled by its own SkillEffect;
 *     reads the mark list from the combat system on enemy wind-up.
 *
 * Phase sequence: toggle active window (4000ms base) → long cooldown (8000ms).
 * Second activation during the window triggers immediate manual return.
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see astral-spec.md § astral_projection
 * @see ADR-064 (attunement shared systems architecture)
 */

import { ToggleSkillEffect } from '../../ToggleSkillEffect.js';
import type { AstralProjectionStateSystem } from '../../../systems/AstralProjectionStateSystem.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface AstralProjectionDeps {
  /** Body/projection separation system (manages entity lifecycle). */
  readonly projection: AstralProjectionStateSystem;
  /**
   * Foresight renderer — only the showExtendedArcs method is required.
   * Called on return when Deathmark is unlocked.
   */
  readonly foresight: {
    showExtendedArcs(entityIds: Iterable<string>, durationMs: number): void;
  };
  /** Status effect application — applies astral_mark on marked enemies at return. */
  readonly statusEffects: {
    applyAstralMark(enemyId: string): void;
  };
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
  };
  /** Provides the player's current world position for body lock. */
  readonly playerPosition: () => { x: number; y: number };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface AstralProjectionConfig {
  /** Base projection window in ms (4000ms). */
  readonly baseDurationMs: number;
  /** Base mark cap — enemies that can be tagged per projection (3). */
  readonly baseMaxMarks: number;
  /** Long cooldown tier in ms (8000ms). */
  readonly cooldownMs: number;
}

const DEFAULT_CONFIG: AstralProjectionConfig = {
  baseDurationMs: 4000,
  baseMaxMarks: 3,
  cooldownMs: 8000,  // long cooldown tier
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class AstralProjectionSkillEffect extends ToggleSkillEffect {
  readonly skillId = 'astral_projection';

  protected readonly deps: AstralProjectionDeps;
  readonly config: AstralProjectionConfig;

  /** Countdown for the current projection window. */
  private _countdownMs = 0;

  /** Resolved window duration set on each activation. */
  private _resolvedDurationMs = 0;

  /** Resolved mark cap set on each activation. */
  private _resolvedMaxMarks = 0;

  /**
   * Enemy IDs marked in the most recent completed projection.
   * Populated by onDeactivate(); cleared on next activation.
   */
  private _lastMarkedEnemyIds: Set<string> = new Set();

  constructor(deps: AstralProjectionDeps, config: Partial<AstralProjectionConfig> = {}) {
    super();
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get countdownMs(): number { return this._countdownMs; }
  get resolvedDurationMs(): number { return this._resolvedDurationMs; }
  get resolvedMaxMarks(): number { return this._resolvedMaxMarks; }
  get lastMarkedEnemyIds(): ReadonlySet<string> { return this._lastMarkedEnemyIds; }

  // ---------------------------------------------------------------------------
  // ToggleSkillEffect implementation
  // ---------------------------------------------------------------------------

  canActivate(): boolean {
    return this.cooldownRemaining <= 0 && !this._isActive;
  }

  /**
   * Activate projection, or — if already active — trigger manual early return.
   *
   * First call: begins projection (body lock + projection spawn).
   * Second call during active window: immediate return (deactivate), starts cooldown.
   */
  activate(): boolean {
    if (this._isActive) {
      // Manual return — reactivating ends the projection immediately.
      this.deactivate();
      return true;
    }

    if (!this.canActivate()) return false;

    this._lastMarkedEnemyIds = new Set();

    // Resolve upgrade bonuses.
    const durationBonus = this.deps.resolver.getAdditiveEffect('astral_projection', 'durationMs');
    this._resolvedDurationMs = this.config.baseDurationMs + durationBonus;
    this._countdownMs = this._resolvedDurationMs;

    const marksBonus = this.deps.resolver.getAdditiveEffect('astral_projection', 'maxMarks');
    this._resolvedMaxMarks = this.config.baseMaxMarks + marksBonus;

    // Begin projection: body locked at current position, projection entity spawned.
    const pos = this.deps.playerPosition();
    this.deps.projection.beginProjection(pos.x, pos.y, this._resolvedMaxMarks, this._resolvedDurationMs);

    this._isActive = true;
    return true;
  }

  /**
   * Mark an enemy during the projection phase.
   * Called externally when the projection entity overlaps an enemy.
   * No-op if the mark cap is already reached.
   */
  markEnemy(enemyId: string): void {
    this.deps.projection.markEnemy(enemyId);
  }

  protected override updateWhileActive(delta: number): void {
    this._countdownMs = Math.max(0, this._countdownMs - delta);
    if (this._countdownMs <= 0) {
      this.deactivate();
    }
  }

  protected override onDeactivate(): void {
    // End projection: body restored, projection despawned; returns mark set.
    const markedEnemyIds = this.deps.projection.endProjection();
    this._lastMarkedEnemyIds = markedEnemyIds;

    // Apply astral_mark to each marked enemy so the combat system can apply
    // the 15% bonus on the player's next skill hit.
    for (const enemyId of markedEnemyIds) {
      this.deps.statusEffects.applyAstralMark(enemyId);
    }

    // Deathmark (astral_projection_upgrade_2): reveal attack arcs continuously.
    const markRevealDuration = this.deps.resolver.getAdditiveEffect(
      'astral_projection', 'markIntentionRevealDurationMs',
    );
    if (markRevealDuration > 0 && markedEnemyIds.size > 0) {
      this.deps.foresight.showExtendedArcs(markedEnemyIds, markRevealDuration);
    }

    // Start long cooldown.
    this.cooldownRemaining = this.config.cooldownMs;
  }

  override destroy(): void {
    if (this._isActive) {
      this.deps.projection.endProjection();
    }
    super.destroy();
  }
}
